import React, { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./cn";

/* ── Slot (asChild support) ──────────────────────────────── */

// Minimal local stand-in for @radix-ui/react-slot, which the reference uses.
// NOT the radix package: in this npm workspace radix hoists to the repo-root
// node_modules and resolves the Vite SPA's react 18, whose Children API
// rejects react-19 elements under vitest (Next aliases react at build time,
// so only tests break — but a primitive that fails its own test suite is
// not shippable). Button only needs the narrow contract: clone the single
// child, merge Button's classes under the child's own, forward props + ref.
type SlotProps = React.HTMLAttributes<HTMLElement> & {
  children?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
};

function Slot({ children, className, ...props }: SlotProps) {
  // toArray + find, not isValidElement(children): JSX hands us an ARRAY the
  // moment the caller renders anything beside the slotted element, and a
  // bare isValidElement check then fails and silently renders nothing.
  const child = React.Children.toArray(children).find(React.isValidElement) as
    | React.ReactElement<{ className?: string }>
    | undefined;
  if (!child) return null;
  return React.cloneElement(child, {
    ...props,
    className: cn(className, child.props.className),
  } as Partial<{ className?: string }>);
}

/* ── Variant definitions ─────────────────────────────────── */

const buttonVariants = cva(
  // Base styles shared by all variants. touch-manipulation: taps must fire
  // immediately on iOS (no double-tap-zoom heuristics) — the reference's
  // wizard nav buttons were intermittently eating the first tap on phones.
  "inline-flex items-center justify-center gap-2 rounded-lg font-semibold cursor-pointer touch-manipulation transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none",
  {
    variants: {
      variant: {
        primary: [
          // text-on-accent (not text-white): VIP dark's accent is a light
          // blue, so the label flips to dark ink for contrast (--c-on-accent).
          // Hover darkens the SAME hue (brand-gradient-cta pattern) instead of
          // swapping to accent-hover — VIP dark's accent-hover (#112353) would
          // fail contrast against the dark ink label.
          "bg-accent text-on-accent",
          "hover:brightness-[0.93] active:brightness-[0.86]",
        ],
        secondary: [
          "bg-transparent border border-edge text-ink-light",
          "hover:text-ink hover:border-accent hover:-translate-y-px",
        ],
        destructive: [
          "bg-red-500 text-white",
          "hover:bg-red-600 hover:-translate-y-0.5",
        ],
        ghost: [
          "bg-transparent text-ink-light",
          "hover:bg-elevated hover:text-ink",
        ],
        cta: [
          "brand-gradient-cta",
        ],
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

/* ── Spinner ─────────────────────────────────────────────── */

function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn("animate-spin", className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

/* ── Button component ────────────────────────────────────── */

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      disabled,
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const classes = cn(buttonVariants({ variant, size }), className);

    // asChild renders the CHILD as the element, wearing Button's styling.
    // It needs its own branch for two reasons: the child owns its content,
    // so no spinner is injected into someone else's element; and `disabled`
    // is not a valid attribute outside form controls, so a slotted <a> takes
    // aria-disabled instead of an attribute browsers ignore.
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={classes}
          aria-disabled={isDisabled || undefined}
          aria-busy={loading || undefined}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={classes}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Spinner />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonProps };
